import React from 'react';
import { Wallet, ArrowRightLeft, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';

export function EscrowDetailModal({
  open, onOpenChange, selectedEscrow, formatDate, setShowResolveModal
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-orange-500" />
            Escrow Details
          </DialogTitle>
        </DialogHeader>
        {selectedEscrow && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Job</p>
                <p className="font-medium">{selectedEscrow.job_title}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Status</p>
                <Badge className={
                  selectedEscrow.status === 'funded' ? 'bg-blue-100 text-blue-800' :
                  selectedEscrow.status === 'released' ? 'bg-green-100 text-green-800' :
                  selectedEscrow.status === 'disputed' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }>
                  {selectedEscrow.status?.replace('_', ' ')}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Contractor</p>
                <p className="font-medium">{selectedEscrow.contractor_name}</p>
                <p className="text-sm text-gray-500">{selectedEscrow.contractor_email}</p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Worker</p>
                <p className="font-medium">{selectedEscrow.worker_name}</p>
                <p className="text-sm text-gray-500">{selectedEscrow.worker_email}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">${(selectedEscrow.total_amount || 0).toLocaleString()}</p>
                <p className="text-sm text-blue-600">Total Amount</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">${(selectedEscrow.funded_amount || 0).toLocaleString()}</p>
                <p className="text-sm text-green-600">Funded</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">${(selectedEscrow.released_amount || 0).toLocaleString()}</p>
                <p className="text-sm text-emerald-600">Released</p>
              </div>
            </div>

            {selectedEscrow.milestones && selectedEscrow.milestones.length > 0 && (
              <div>
                <p className="font-medium mb-2">Milestones ({selectedEscrow.milestones.length})</p>
                <div className="space-y-2">
                  {selectedEscrow.milestones.map((ms, idx) => (
                    <div key={`ms-${selectedEscrow.id}-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{ms.title || `Milestone ${idx + 1}`}</p>
                        <p className="text-sm text-gray-500">${(ms.amount || 0).toLocaleString()}</p>
                      </div>
                      <Badge className={
                        ms.status === 'paid' ? 'bg-green-100 text-green-800' :
                        ms.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {ms.status || 'pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedEscrow.dispute_reason && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Dispute Reason</p>
                    <p className="text-sm text-red-700">{selectedEscrow.dispute_reason}</p>
                    {selectedEscrow.dispute_opened_at && (
                      <p className="text-xs text-red-500 mt-1">Opened: {formatDate(selectedEscrow.dispute_opened_at)}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                Created: {formatDate(selectedEscrow.created_at)}
              </div>
              <div className="flex gap-2">
                {selectedEscrow.status === 'disputed' && (
                  <Button onClick={() => setShowResolveModal(true)} className="bg-orange-500 hover:bg-orange-600">
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Resolve Dispute
                  </Button>
                )}
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ResolveDisputeModal({
  open, onOpenChange, resolveType, setResolveType,
  splitPercentage, setSplitPercentage, adminNote, setAdminNote,
  handleResolveDispute, actionLoading
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-orange-500" />
            Resolve Dispute
          </DialogTitle>
          <DialogDescription>
            Choose how to resolve this escrow dispute. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Resolution Type</Label>
            <Select value={resolveType} onValueChange={setResolveType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="release_to_worker">Release Full Amount to Worker</SelectItem>
                <SelectItem value="refund_to_contractor">Refund Full Amount to Contractor</SelectItem>
                <SelectItem value="split">Split Between Both Parties</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {resolveType === 'split' && (
            <div>
              <Label>Worker&apos;s Percentage: {splitPercentage}%</Label>
              <input
                type="range"
                min="0"
                max="100"
                value={splitPercentage}
                onChange={(e) => setSplitPercentage(Number(e.target.value))}
                className="w-full mt-2"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>Worker: {splitPercentage}%</span>
                <span>Contractor: {100 - splitPercentage}%</span>
              </div>
            </div>
          )}

          <div>
            <Label>Admin Note</Label>
            <Textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Explain the resolution decision..."
              className="mt-1"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleResolveDispute} disabled={actionLoading} className="bg-orange-500 hover:bg-orange-600">
            {actionLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Confirm Resolution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
